import { useGA4Core } from '@/composables/common/analytics/useGA4Core';

/**
 * useAbonoTracking
 * Domain-specific facade for Abono (Subscription) purchase tracking in GA4.
 *
 * This composable handles the mapping of abono-specific data structures to GA4 standard DTOs.
 * It delegates all GA4 tracking calls to useGA4Core for validation and dataLayer management.
 *
 * Note: Abonos differ from eventos:
 * - Direct checkout (no listing events)
 * - Single subscription object at lista_abonados[0]
 * - No view_item_list or view_item events
 *
 * Usage:
 *   const { trackBeginCheckout, trackAddPaymentInfo, trackPurchase } = useAbonoTracking({ store, getVuexAlias });
 *   await trackPurchase(abonoResumen, tpvOrder, sessionId);
 */

/**
 * Maps abono subscription to GA4 format items
 * For abonos with asientos: creates one item per asiento (each with quantity=1, individual price)
 * For SGA with items_carrito: creates one item per carrito item with precio.amount
 * For normal abono with datos_guardados: creates one item per item with descuento.precio
 * For abonos without any array: creates one aggregated item with summed price
 * 
 * Handles multiple pricing sources (priority):
 *   1. asientos array: each asiento becomes a separate item with asientos[i].precio.precio
 *   2. itemsCarrito array with SGA schema: each item with item.precio.amount (SGA flow)
 *   3. itemsCarrito array with normal abono schema: each item with item.descuento.precio (normal abono flow)
 *   4. lista_abonados[0].precio_unitario: single item with unit price
 *   5. resumen_total fallback: single item with total price
 * 
 * @param {Object} abonoResumen - The abono purchase summary
 * @param {Array} asientos - Optional asientos array with nested precio.precio values
 * @param {Array} itemsCarrito - Optional items array: can be SGA (precio.amount) or normal abono (descuento.precio)
 * @returns {Array} GA4-formatted items array
 */
const mapAbonoItems = (abonoResumen, asientos = null, itemsCarrito = null) => {
    const items = [];
    
    const abonado = abonoResumen.lista_abonados?.[0];

    if (!abonado) {
        console.warn('[AbonoTracking] mapAbonoItems - No abonado found');
        return items;
    }

    // Strategy 1: If asientos array provided, create one item per asiento
    if (asientos && Array.isArray(asientos) && asientos.length > 0) {
        asientos.forEach((asiento, index) => {
            // datos_compradores has flat .precio (not nested), but handle both cases
            const price = parseFloat(asiento?.precio?.precio || asiento?.precio || 0);
            items.push({
                item_id: String(abonoResumen.abono_id || ''),
                item_name: abonoResumen.abono_name || 'Abono',
                price: price,
                quantity: 1,
                item_category: 'Abono',
            });
        });
    } 
    // Strategy 2: If items_carrito array provided, create one item per carrito item
    else if (itemsCarrito && Array.isArray(itemsCarrito) && itemsCarrito.length > 0) {
        // Detect which schema: SGA (precio.amount) vs normal abono (descuento.precio)
        const isSGASchema = itemsCarrito[0]?.precio?.amount !== undefined;
        const isAbonoSchema = itemsCarrito[0]?.descuento?.precio !== undefined;
        
        if (isSGASchema) {
            itemsCarrito.forEach((carritoItem, index) => {
                const price = parseFloat(carritoItem?.precio?.amount || 0);
                items.push({
                    item_id: String(abonoResumen.abono_id || ''),
                    item_name: abonoResumen.abono_name || 'Abono',
                    price: price,
                    quantity: 1,
                    item_category: 'Abono',
                });
            });
        } else if (isAbonoSchema) {
            itemsCarrito.forEach((datosItem, index) => {
                const price = parseFloat(datosItem?.descuento?.precio || 0);
                items.push({
                    item_id: String(abonoResumen.abono_id || ''),
                    item_name: abonoResumen.abono_name || 'Abono',
                    price: price,
                    quantity: 1,
                    item_category: 'Abono',
                });
            });
        } else {
            console.warn('[AbonoTracking] mapAbonoItems - Unknown items_carrito schema, fallback to precio.amount');
            itemsCarrito.forEach((carritoItem, index) => {
                const price = parseFloat(carritoItem?.precio?.amount || 0);
                items.push({
                    item_id: String(abonoResumen.abono_id || ''),
                    item_name: abonoResumen.abono_name || 'Abono',
                    price: price,
                    quantity: 1,
                    item_category: 'Abono',
                });
            });
        }
    }
    // Strategy 3: Use precio_unitario from abonado
    else if (abonado.precio_unitario) {
        const price = parseFloat(abonado.precio_unitario);
        items.push({
            item_id: String(abonoResumen.abono_id || ''),
            item_name: abonoResumen.abono_name || 'Abono',
            price: price,
            quantity: 1,
            item_category: 'Abono',
        });
    }
    // Strategy 4: Fallback to resumen_total
    else if (abonoResumen.resumen_total) {
        const price = parseFloat(abonoResumen.resumen_total);
        items.push({
            item_id: String(abonoResumen.abono_id || ''),
            item_name: abonoResumen.abono_name || 'Abono',
            price: price,
            quantity: 1,
            item_category: 'Abono',
        });
    }

    return items;
};

/**
 * Safely extracts transaction ID with fallback strategy
 * @param {Object} abonoResumen - The abono purchase summary
 * @returns {String} Transaction ID
 */
const extractTransactionId = abonoResumen => {
    if (abonoResumen.tpv_order && typeof abonoResumen.tpv_order === 'string') {
        return abonoResumen.tpv_order;
    }

    if (abonoResumen.codigo && typeof abonoResumen.codigo === 'string') {
        return abonoResumen.codigo;
    }

    return `abono-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export function useAbonoTracking(deps = {}) {
    // Normalize and validate dependencies
    const { store, getVuexAlias } = deps;
    
    if (!store) {
        console.warn('[AbonoTracking] useAbonoTracking called without store dependency. GA4 tracking will not function.');
    }
    
    const ga4Core = useGA4Core(store, getVuexAlias);

    /**
     * trackBeginCheckout(abonoResumen, asientos, itemsCarrito)
     * Track checkout flow start for abono purchase
     * @param {Object} abonoResumen - Abono purchase summary with lista_abonados, resumen_total, items_carrito, etc.
     * @param {Array} asientos - Optional asientos array for price calculation
     * @param {Array} itemsCarrito - Optional items_carrito array (SGA flow) for price calculation
     * @returns {Boolean} Success indicator
     */
    const trackBeginCheckout = (abonoResumen, asientos = null, itemsCarrito = null) => {
        try {
            if (!abonoResumen || !abonoResumen.abono_id || !abonoResumen.abono_name) {
                console.warn('[AbonoTracking] trackBeginCheckout - Invalid abonoResumen');
                return false;
            }

            // Use items_carrito from parameter if available, otherwise from abonoResumen
            const carrito = itemsCarrito || abonoResumen.items_carrito;
            const items = mapAbonoItems(abonoResumen, asientos, carrito);

            if (items.length === 0) {
                console.warn('[AbonoTracking] trackBeginCheckout - No valid items mapped from abonoResumen');
                return false;
            }

            // Calculate value: prefer canonical resumen_total over item recomputation
            let value;
            if (abonoResumen.resumen_total && abonoResumen.resumen_total > 0) {
                // Caller provided canonical total—use it (includes shipping, parcelas, all discounts)
                value = parseFloat(abonoResumen.resumen_total);
            } else {
                // Fallback: compute from items if no canonical total
                value = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
            }

            const dto = {
                value,
                currency: abonoResumen.moneda || 'EUR',
                items,
            };

            return ga4Core.trackBeginCheckout(dto);
        } catch (error) {
            console.error('[AbonoTracking] trackBeginCheckout - Error:', error);
            return false;
        }
    };

    /**
     * trackAddPaymentInfo(abonoResumen, paymentMethod, asientos, itemsCarrito)
     * Track payment method selection during abono checkout
     * Supports both SGA flow (items_carrito with precio.amount) and normal abono flow (datos_guardados with descuento.precio)
     * @param {Object} abonoResumen - Abono purchase summary
     * @param {String} paymentMethod - Selected payment method (optional)
     * @param {Array} asientos - Optional asientos array for price calculation
     * @param {Array} itemsCarrito - Optional items array: can be SGA items (precio.amount) or normal abono datos_guardados (descuento.precio)
     * @returns {Boolean} Success indicator
     */
    const trackAddPaymentInfo = (abonoResumen, paymentMethod = '', asientos = null, itemsCarrito = null) => {
        try {
            if (!abonoResumen || !abonoResumen.abono_id || !abonoResumen.abono_name) {
                console.warn('[AbonoTracking] trackAddPaymentInfo - Invalid abonoResumen');
                return false;
            }

            // Use items_carrito from parameter if available, otherwise from abonoResumen
            const carrito = itemsCarrito || abonoResumen.items_carrito;
            const items = mapAbonoItems(abonoResumen, asientos, carrito);

            if (items.length === 0) {
                console.warn('[AbonoTracking] trackAddPaymentInfo - No valid items mapped from abonoResumen');
                return false;
            }

            // Calculate value: prefer canonical resumen_total over item recomputation
            let value;
            if (abonoResumen.resumen_total && abonoResumen.resumen_total > 0) {
                // Caller provided canonical total—use it (includes shipping, parcelas, all discounts)
                value = parseFloat(abonoResumen.resumen_total);
            } else {
                // Fallback: compute from items if no canonical total
                value = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
            }

            const dto = {
                value,
                currency: abonoResumen.moneda || 'EUR',
                items,
            };

            return ga4Core.trackAddPaymentInfo(dto, paymentMethod);
        } catch (error) {
            console.error('[AbonoTracking] trackAddPaymentInfo - Error:', error);
            return false;
        }
    };

    /**
     * trackPurchase(abonoResumen, tpvOrder, sessionId, asientos, itemsCarrito) - ASYNC
     * Track completed purchase event for abono
     * Delegates to ga4Core.trackPurchase with tipo='abonos'
     * @param {Object} abonoResumen - Abono purchase summary with resumen_total, lista_abonados, items_carrito, etc.
     * @param {String} tpvOrder - TPV order ID from payment processor (REAL transaction_id for GA4)
     * @param {String} sessionId - Session ID for user tracking
     * @param {Array} asientos - Optional asientos array for price calculation
     * @param {Array} itemsCarrito - Optional items_carrito array (SGA flow) for price calculation
     * @returns {Promise<Boolean>} Success indicator
     */
    const trackPurchase = async(abonoResumen, tpvOrder, sessionId, asientos = null, itemsCarrito = null) => {
        try {
            if (!abonoResumen || !abonoResumen.abono_id || !abonoResumen.abono_name) {
                console.warn('[AbonoTracking] trackPurchase - Invalid abonoResumen');
                return false;
            }

            if (!tpvOrder || typeof tpvOrder !== 'string') {
                console.warn('[AbonoTracking] trackPurchase - Invalid tpvOrder');
                return false;
            }

            if (!sessionId || typeof sessionId !== 'string') {
                console.warn('[AbonoTracking] trackPurchase - Invalid sessionId');
                return false;
            }

            // Use items_carrito from parameter if available, otherwise from abonoResumen
            const carrito = itemsCarrito || abonoResumen.items_carrito;
            const items = mapAbonoItems(abonoResumen, asientos, carrito);

            if (items.length === 0) {
                console.warn('[AbonoTracking] trackPurchase - No valid items mapped from abonoResumen');
                return false;
            }

            // Calculate value: prefer canonical resumen_total over item recomputation
            let value;
            if (abonoResumen.resumen_total && abonoResumen.resumen_total > 0) {
                // Caller provided canonical total—use it (includes shipping, parcelas, all discounts)
                value = parseFloat(abonoResumen.resumen_total);
            } else {
                // Fallback: compute from items if no canonical total
                value = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
            }

            // Warn if computed items total differs significantly from provided resumen_total
            if (abonoResumen.resumen_total && abonoResumen.resumen_total > 0) {
                const computed = items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
                if (computed !== abonoResumen.resumen_total) {
                    console.warn(`[AbonoTracking] trackPurchase - WARNING: Mismatch detected: resumen_total=${abonoResumen.resumen_total}, items_computed=${computed}. Using resumen_total as canonical.`);
                }
            }

            // Use the real tpvOrder as transactionId (NOT synthetic)
            const dto = {
                value,
                currency: abonoResumen.moneda || 'EUR',
                items,
                transactionId: tpvOrder,
            };

            return await ga4Core.trackPurchase(dto, tpvOrder, sessionId, 'abonos');
        } catch (error) {
            console.error('[AbonoTracking] trackPurchase - Error:', error);
            return false;
        }
    };

    // ─── Instant Credit tracking wrappers ────────────────────────────────────
    // 8 custom GA4 events for the PAYCOMET Instant Credit flow (abono-only).
    // All delegate to ga4Core.trackCustomEvent (no DTO validation).

    const trackIcSelectorViewed = () => {
        return ga4Core.trackCustomEvent('instant_credit_selector_viewed');
    };

    const trackIcCuotasSelected = (nCuotas, importeTotal) => {
        return ga4Core.trackCustomEvent('instant_credit_cuotas_selected', {
            n_cuotas: nCuotas,
            importe_total: importeTotal,
        });
    };

    const trackIcInitiationSuccess = () => {
        return ga4Core.trackCustomEvent('instant_credit_initiation_success');
    };

    const trackIcInitiationFailed = errorCode => {
        return ga4Core.trackCustomEvent('instant_credit_initiation_failed', {
            error_code: errorCode || 'unknown',
        });
    };

    const trackIcChallengeRedirected = () => {
        return ga4Core.trackCustomEvent('instant_credit_challenge_redirected');
    };

    const trackIcReturnOkViewed = () => {
        return ga4Core.trackCustomEvent('instant_credit_return_ok_viewed');
    };

    const trackIcReturnKoViewed = () => {
        return ga4Core.trackCustomEvent('instant_credit_return_ko_viewed');
    };

    const trackIcPollFinalState = state => {
        return ga4Core.trackCustomEvent('instant_credit_poll_final_state', {
            state: state || 'unknown',
        });
    };

    return {
        trackBeginCheckout,
        trackAddPaymentInfo,
        trackPurchase,
        trackIcSelectorViewed,
        trackIcCuotasSelected,
        trackIcInitiationSuccess,
        trackIcInitiationFailed,
        trackIcChallengeRedirected,
        trackIcReturnOkViewed,
        trackIcReturnKoViewed,
        trackIcPollFinalState,
    };
}
